import { useMemo } from 'react';
import { useSeoMeta } from '@unhead/react';
import { MessageCircle } from 'lucide-react';
import type { NostrEvent } from '@nostrify/nostrify';

import { AppShell } from '@/components/AppShell';
import { CommentsSection } from '@/components/comments/CommentsSection';
import { HighlightCard } from '@/components/HighlightCard';
import { Card, CardContent } from '@/components/ui/card';
import { useEngagement } from '@/hooks/useEngagement';
import { useHighlightSources } from '@/hooks/useHighlightSources';
import { parseHighlight } from '@/lib/highlights';

interface HighlightDetailPageProps {
  event: NostrEvent;
}

/** Permalink view for a single highlight: full card + comment thread. */
export function HighlightDetailPage({ event }: HighlightDetailPageProps) {
  const highlight = useMemo(() => parseHighlight(event), [event]);

  const highlights = useMemo(() => (highlight ? [highlight] : []), [highlight]);
  const sources = useHighlightSources(highlights);
  const engagement = useEngagement(highlights);

  useSeoMeta({
    title: 'Highlight — glowmark',
    description: highlight?.quote.slice(0, 160) || 'A highlighted passage on glowmark.',
  });

  if (!highlight) {
    return (
      <AppShell>
        <div className="container max-w-2xl py-16">
          <Card className="border-dashed">
            <CardContent className="py-12 px-8 text-center">
              <p className="text-muted-foreground max-w-sm mx-auto">
                This event doesn't look like a usable highlight.
              </p>
            </CardContent>
          </Card>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="container max-w-2xl py-8 space-y-8">
        <HighlightCard
          highlight={highlight}
          sources={sources.data}
          sourcesLoading={sources.isPending}
          stats={engagement.data?.get(highlight.event.id)}
        />

        <section className="space-y-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold px-1">
            <MessageCircle className="size-4.5 text-primary" />
            Discussion
          </h2>
          <CommentsSection root={event} />
        </section>
      </div>
    </AppShell>
  );
}
